/**
 * Complete Sanskrit Numbers Dataset (संस्कृत-संख्या-परिचयः)
 * 1 to 100 complete numbers, gender declensions (1-4), ordinals,
 * large Vedic powers of 10, and morphological construction rules.
 */

export interface SanskritNumberItem {
  value: number;
  devanagariNumeral: string;
  word: string;
  iast: string;
  english: string;
  decadeGroup: string; // e.g. '1-10', '11-20', etc.
  decadeLabelDev: string; // 'प्रथमावलिः (१-१०)'
  breakdown?: string;
  alternatives?: string[];
  notes?: string;
}

export interface GenderNumberExample {
  number: number;
  devanagariNumeral: string;
  stem: string;
  masculine: {
    form: string;
    iast: string;
    example: string;
    exampleEn: string;
  };
  feminine: {
    form: string;
    iast: string;
    example: string;
    exampleEn: string;
  };
  neuter: {
    form: string;
    iast: string;
    example: string;
    exampleEn: string;
  };
  grammaticalRule: string;
}

export interface OrdinalNumberItem {
  rank: number;
  numeral: string;
  masculine: string;
  feminine: string;
  neuter: string;
  iast: string;
  english: string;
  example: string;
}

export interface VedicLargeNumberItem {
  exponent: number;
  powerOfTen: string;
  numericDisplay: string;
  sanskritName: string;
  iast: string;
  englishMeaning: string;
  source: string;
  description: string;
}

// 0 to 100 Comprehensive Sanskrit Numbers
export const SANSKRIT_NUMBERS_1_TO_100: SanskritNumberItem[] = [
  {
    value: 0,
    devanagariNumeral: '०',
    word: 'शून्यम्',
    iast: 'śūnyam',
    english: 'Zero',
    decadeGroup: '1-10',
    decadeLabelDev: 'प्रथमावलिः (१-१०)',
    breakdown: 'शून् + यत् (Void, dot, cipher)',
    notes: 'Discovered in India as both a placeholder and an operational mathematical entity.',
  },
  {
    value: 1,
    devanagariNumeral: '१',
    word: 'एकम्',
    iast: 'ekam',
    english: 'One',
    decadeGroup: '1-10',
    decadeLabelDev: 'प्रथमावलिः (१-१०)',
    breakdown: 'Root: एक (neuter base form एकम्)',
    notes: 'Declines in all 3 genders: एकः (M), एका (F), एकम् (N). Always singular.',
  },
  {
    value: 2,
    devanagariNumeral: '२',
    word: 'द्वे',
    iast: 'dve',
    english: 'Two',
    decadeGroup: '1-10',
    decadeLabelDev: 'प्रथमावलिः (१-१०)',
    breakdown: 'Root: द्वि (feminine/neuter base form द्वे)',
    notes: 'Declines in all 3 genders: द्वौ (M), द्वे (F), द्वे (N). Always dual (द्विवचन).',
  },
  {
    value: 3,
    devanagariNumeral: '३',
    word: 'त्रीणि',
    iast: 'trīṇi',
    english: 'Three',
    decadeGroup: '1-10',
    decadeLabelDev: 'प्रथमावलिः (१-१०)',
    breakdown: 'Root: त्रि (neuter base form त्रीणि)',
    notes: 'Declines in all 3 genders: त्रयः (M), तिस्रः (F), त्रीणि (N). Always plural (बहुवचन).',
  },
  {
    value: 4,
    devanagariNumeral: '४',
    word: 'चत्वारि',
    iast: 'catvāri',
    english: 'Four',
    decadeGroup: '1-10',
    decadeLabelDev: 'प्रथमावलिः (१-१०)',
    breakdown: 'Root: चतुर् (neuter base form चत्वारि)',
    notes: 'Declines in all 3 genders: चत्वारः (M), चतस्रः (F), चत्वारि (N). Always plural.',
  },
  {
    value: 5,
    devanagariNumeral: '५',
    word: 'पञ्च',
    iast: 'pañca',
    english: 'Five',
    decadeGroup: '1-10',
    decadeLabelDev: 'प्रथमावलिः (१-१०)',
    breakdown: 'Root: पञ्चन्',
    notes: 'From 5 onwards, numbers have identical forms across all 3 genders (त्रिषु लिङ्गेषु समानानि).',
  },
  {
    value: 6,
    devanagariNumeral: '६',
    word: 'षट्',
    iast: 'ṣaṭ',
    english: 'Six',
    decadeGroup: '1-10',
    decadeLabelDev: 'प्रथमावलिः (१-१०)',
    breakdown: 'Root: षष् (takes form षट् / षड् in compounds)',
    notes: 'Retroflex voiceless stop. Changes to षड् before voiced sounds and षण् before nasals.',
  },
  {
    value: 7,
    devanagariNumeral: '७',
    word: 'सप्त',
    iast: 'sapta',
    english: 'Seven',
    decadeGroup: '1-10',
    decadeLabelDev: 'प्रथमावलिः (१-१०)',
    breakdown: 'Root: सप्तन्',
    notes: 'Cognate with Latin septem and Greek hepta.',
  },
  {
    value: 8,
    devanagariNumeral: '८',
    word: 'अष्ट',
    iast: 'aṣṭa',
    english: 'Eight',
    decadeGroup: '1-10',
    decadeLabelDev: 'प्रथमावलिः (१-१०)',
    breakdown: 'Root: अष्टन् (also appears as अष्टौ in nominative)',
    alternatives: ['अष्टौ'],
    notes: 'Both अष्ट and अष्टौ are valid nominative plural forms in classical Paninian grammar.',
  },
  {
    value: 9,
    devanagariNumeral: '९',
    word: 'नव',
    iast: 'nava',
    english: 'Nine',
    decadeGroup: '1-10',
    decadeLabelDev: 'प्रथमावलिः (१-१०)',
    breakdown: 'Root: नवन्',
    notes: 'Cognate with Latin novem and English nine.',
  },
  {
    value: 10,
    devanagariNumeral: '१०',
    word: 'दश',
    iast: 'daśa',
    english: 'Ten',
    decadeGroup: '1-10',
    decadeLabelDev: 'प्रथमावलिः (१-१०)',
    breakdown: 'Root: दशन् (foundation of the decimal system)',
    notes: 'All numbers from 11 to 19 end with the suffix -दश (-daśa).',
  },

  // 11 to 20
  {
    value: 11,
    devanagariNumeral: '११',
    word: 'एकादश',
    iast: 'ekādaśa',
    english: 'Eleven',
    decadeGroup: '11-20',
    decadeLabelDev: 'द्वितीयावलिः (११-२०)',
    breakdown: 'एक (1) + दश (10) = एकादश',
    notes: 'Long vowel ā in ekā- arises from compound vowel lengthening.',
  },
  {
    value: 12,
    devanagariNumeral: '१२',
    word: 'द्वादश',
    iast: 'dvādaśa',
    english: 'Twelve',
    decadeGroup: '11-20',
    decadeLabelDev: 'द्वितीयावलिः (११-२०)',
    breakdown: 'द्वा (2) + दश (10) = द्वादश',
    notes: 'Root द्वि becomes द्वा- before दश.',
  },
  {
    value: 13,
    devanagariNumeral: '१३',
    word: 'त्रयोदश',
    iast: 'trayodaśa',
    english: 'Thirteen',
    decadeGroup: '11-20',
    decadeLabelDev: 'द्वितीयावलिः (११-२०)',
    breakdown: 'त्रयः (3) + दश (10) = त्रयोदश',
    notes: 'Visarga संधि: त्रयः + दश -> त्रयोदश (ah + d -> o + d).',
  },
  {
    value: 14,
    devanagariNumeral: '१४',
    word: 'चतुर्दश',
    iast: 'caturdaśa',
    english: 'Fourteen',
    decadeGroup: '11-20',
    decadeLabelDev: 'द्वितीयावलिः (११-२०)',
    breakdown: 'चतुर् (4) + दश (10) = चतुर्दश',
    notes: 'Repha (र्) sits over the following consonant द.',
  },
  {
    value: 15,
    devanagariNumeral: '१५',
    word: 'पञ्चदश',
    iast: 'pañcadaśa',
    english: 'Fifteen',
    decadeGroup: '11-20',
    decadeLabelDev: 'द्वितीयावलिः (११-२०)',
    breakdown: 'पञ्च (5) + दश (10) = पञ्चदश',
    notes: 'Simple compound without Sandhi modification.',
  },
  {
    value: 16,
    devanagariNumeral: '१६',
    word: 'षोडश',
    iast: 'ṣoḍaśa',
    english: 'Sixteen',
    decadeGroup: '11-20',
    decadeLabelDev: 'द्वितीयावलिः (११-२०)',
    breakdown: 'षष् (6) + दश (10) = षोडश',
    notes: 'Special archaic Sandhi: ṣaṣ + daśa transforms into ṣoḍaśa.',
  },
  {
    value: 17,
    devanagariNumeral: '१७',
    word: 'सप्तदश',
    iast: 'saptadaśa',
    english: 'Seventeen',
    decadeGroup: '11-20',
    decadeLabelDev: 'द्वितीयावलिः (११-२०)',
    breakdown: 'सप्त (7) + दश (10) = सप्तदश',
    notes: 'Direct compound formation.',
  },
  {
    value: 18,
    devanagariNumeral: '१८',
    word: 'अष्टादश',
    iast: 'aṣṭādaśa',
    english: 'Eighteen',
    decadeGroup: '11-20',
    decadeLabelDev: 'द्वितीयावलिः (११-२०)',
    breakdown: 'अष्ट (8) + दश (10) = अष्टादश',
    notes: 'Lengthening of a to ā: aṣṭā-daśa (like the 18 Parvas of Mahabharata).',
  },
  {
    value: 19,
    devanagariNumeral: '१९',
    word: 'नवदश',
    iast: 'navadaśa',
    english: 'Nineteen',
    decadeGroup: '11-20',
    decadeLabelDev: 'द्वितीयावलिः (११-२०)',
    breakdown: 'नव (9) + दश (10) / एकोनविंशतिः (one less than 20)',
    alternatives: ['एकोनविंशतिः', 'ऊनविंशतिः'],
    notes: 'In Sanskrit, 19 can be called 9+10 (नवदश) or 20 minus 1 (एकोनविंशतिः).',
  },
  {
    value: 20,
    devanagariNumeral: '२०',
    word: 'विंशतिः',
    iast: 'viṁśatiḥ',
    english: 'Twenty',
    decadeGroup: '11-20',
    decadeLabelDev: 'द्वितीयावलिः (११-२०)',
    breakdown: 'विंशति (Feminine singular substantive noun)',
    notes: 'Grammatically feminine singular, but qualifies plural objects: विंशतिः बालकाः.',
  },

  // 21 to 30
  {
    value: 21,
    devanagariNumeral: '२१',
    word: 'एकविंशतिः',
    iast: 'ekaviṁśatiḥ',
    english: 'Twenty-One',
    decadeGroup: '21-30',
    decadeLabelDev: 'तृतीयावलिः (२१-३०)',
    breakdown: 'एक (1) + विंशतिः (20)',
  },
  {
    value: 22,
    devanagariNumeral: '२२',
    word: 'द्वाविंशतिः',
    iast: 'dvāviṁśatiḥ',
    english: 'Twenty-Two',
    decadeGroup: '21-30',
    decadeLabelDev: 'तृतीयावलिः (२१-३०)',
    breakdown: 'द्वा (2) + विंशतिः (20)',
  },
  {
    value: 23,
    devanagariNumeral: '२३',
    word: 'त्रयोविंशतिः',
    iast: 'trayoviṁśatiḥ',
    english: 'Twenty-Three',
    decadeGroup: '21-30',
    decadeLabelDev: 'तृतीयावलिः (२१-३०)',
    breakdown: 'त्रयः (3) + विंशतिः (20) -> त्रयोविंशतिः',
  },
  {
    value: 24,
    devanagariNumeral: '२४',
    word: 'चतुर्विंशतिः',
    iast: 'caturviṁśatiḥ',
    english: 'Twenty-Four',
    decadeGroup: '21-30',
    decadeLabelDev: 'तृतीयावलिः (२१-३०)',
    breakdown: 'चतुर् (4) + विंशतिः (20)',
    notes: 'Famous in the 24 syllables of Gayatri Mantra (चतुर्विंशत्यक्षरा गायत्री).',
  },
  {
    value: 25,
    devanagariNumeral: '२५',
    word: 'पञ्चविंशतिः',
    iast: 'pañcaviṁśatiḥ',
    english: 'Twenty-Five',
    decadeGroup: '21-30',
    decadeLabelDev: 'तृतीयावलिः (२१-३०)',
    breakdown: 'पञ्च (5) + विंशतिः (20)',
  },
  {
    value: 26,
    devanagariNumeral: '२६',
    word: 'षड्विंशतिः',
    iast: 'ṣaḍviṁśatiḥ',
    english: 'Twenty-Six',
    decadeGroup: '21-30',
    decadeLabelDev: 'तृतीयावलिः (२१-३०)',
    breakdown: 'षट् (6) + विंशतिः (20) -> षड् (voiced before v)',
    notes: 'Notice ṭ becomes voiced retroflex stop ḍ before semi-vowel v.',
  },
  {
    value: 27,
    devanagariNumeral: '२७',
    word: 'सप्तविंशतिः',
    iast: 'saptaviṁśatiḥ',
    english: 'Twenty-Seven',
    decadeGroup: '21-30',
    decadeLabelDev: 'तृतीयावलिः (२१-३०)',
    breakdown: 'सप्त (7) + विंशतिः (20)',
    notes: 'The number of lunar mansions or Nakshatras in Indian astronomy (सप्तविंशति नक्षत्राणि).',
  },
  {
    value: 28,
    devanagariNumeral: '२८',
    word: 'अष्टाविंशतिः',
    iast: 'aṣṭāviṁśatiḥ',
    english: 'Twenty-Eight',
    decadeGroup: '21-30',
    decadeLabelDev: 'तृतीयावलिः (२१-३०)',
    breakdown: 'अष्टा (8) + विंशतिः (20)',
  },
  {
    value: 29,
    devanagariNumeral: '२९',
    word: 'नवविंशतिः',
    iast: 'navaviṁśatiḥ',
    english: 'Twenty-Nine',
    decadeGroup: '21-30',
    decadeLabelDev: 'तृतीयावलिः (२१-३०)',
    breakdown: 'नव (9) + विंशतिः (20) / एकोनत्रिंशत् (1 less than 30)',
    alternatives: ['एकोनत्रिंशत्', 'ऊनत्रिंशत्'],
  },
  {
    value: 30,
    devanagariNumeral: '३०',
    word: 'त्रिंशत्',
    iast: 'triṁśat',
    english: 'Thirty',
    decadeGroup: '21-30',
    decadeLabelDev: 'तृतीयावलिः (२१-३०)',
    breakdown: 'त्रिंशत् (Feminine consonant-ending noun)',
    notes: 'Numbers ending in -त् (-t) are feminine singular nouns in Sanskrit grammar.',
  },

  // 31 to 40
  {
    value: 31,
    devanagariNumeral: '३१',
    word: 'एकत्रिंशत्',
    iast: 'ekatriṁśat',
    english: 'Thirty-One',
    decadeGroup: '31-40',
    decadeLabelDev: 'चतुर्थावलिः (३१-४०)',
    breakdown: 'एक (1) + त्रिंशत् (30)',
  },
  {
    value: 32,
    devanagariNumeral: '३२',
    word: 'द्वात्रिंशत्',
    iast: 'dvātriṁśat',
    english: 'Thirty-Two',
    decadeGroup: '31-40',
    decadeLabelDev: 'चतुर्थावलिः (३१-४०)',
    breakdown: 'द्वा (2) + त्रिंशत् (30)',
    alternatives: ['द्वित्रिंशत्'],
    notes: 'Traditional 32 dental and physical marks (द्वात्रिंशल्लक्षण).',
  },
  {
    value: 33,
    devanagariNumeral: '३३',
    word: 'त्रयस्त्रिंशत्',
    iast: 'trayastriṁśat',
    english: 'Thirty-Three',
    decadeGroup: '31-40',
    decadeLabelDev: 'चतुर्थावलिः (३१-४०)',
    breakdown: 'त्रयः + त्रिंशत् -> त्रयस्त्रिंशत् (visarga becomes dental s before t)',
    notes: 'The 33 Vedic celestial divinities (त्रयस्त्रिंशद् देवाः).',
  },
  {
    value: 34,
    devanagariNumeral: '३४',
    word: 'चतुस्त्रिंशत्',
    iast: 'catustriṁśat',
    english: 'Thirty-Four',
    decadeGroup: '31-40',
    decadeLabelDev: 'चतुर्थावलिः (३१-४०)',
    breakdown: 'चतुः + त्रिंशत् -> चतुस्त्रिंशत्',
  },
  {
    value: 35,
    devanagariNumeral: '३५',
    word: 'पञ्चत्रिंशत्',
    iast: 'pañcatriṁśat',
    english: 'Thirty-Five',
    decadeGroup: '31-40',
    decadeLabelDev: 'चतुर्थावलिः (३१-४०)',
    breakdown: 'पञ्च (5) + त्रिंशत् (30)',
  },
  {
    value: 36,
    devanagariNumeral: '३६',
    word: 'षट्त्रिंशत्',
    iast: 'ṣaṭtriṁśat',
    english: 'Thirty-Six',
    decadeGroup: '31-40',
    decadeLabelDev: 'चतुर्थावलिः (३१-४०)',
    breakdown: 'षट् (6) + त्रिंशत् (30)',
  },
  {
    value: 37,
    devanagariNumeral: '३७',
    word: 'सप्तत्रिंशत्',
    iast: 'saptatriṁśat',
    english: 'Thirty-Seven',
    decadeGroup: '31-40',
    decadeLabelDev: 'चतुर्थावलिः (३१-४०)',
    breakdown: 'सप्त (7) + त्रिंशत् (30)',
  },
  {
    value: 38,
    devanagariNumeral: '३८',
    word: 'अष्टात्रिंशत्',
    iast: 'aṣṭātriṁśat',
    english: 'Thirty-Eight',
    decadeGroup: '31-40',
    decadeLabelDev: 'चतुर्थावलिः (३१-४०)',
    breakdown: 'अष्टा (8) + त्रिंशत् (30)',
    alternatives: ['अष्टत्रिंशत्'],
  },
  {
    value: 39,
    devanagariNumeral: '३९',
    word: 'नवत्रिंशत्',
    iast: 'navatriṁśat',
    english: 'Thirty-Nine',
    decadeGroup: '31-40',
    decadeLabelDev: 'चतुर्थावलिः (३१-४०)',
    breakdown: 'नव (9) + त्रिंशत् (30) / एकोनचत्वारिंशत् (1 less than 40)',
    alternatives: ['एकोनचत्वारिंशत्', 'ऊनचत्वारिंशत्'],
  },
  {
    value: 40,
    devanagariNumeral: '४०',
    word: 'चत्वारिंशत्',
    iast: 'catvāriṁśat',
    english: 'Forty',
    decadeGroup: '31-40',
    decadeLabelDev: 'चतुर्थावलिः (३१-४०)',
    breakdown: 'चत्वारिंशत् (Feminine noun ending in t)',
  },

  // 41 to 50
  {
    value: 41,
    devanagariNumeral: '४१',
    word: 'एकचत्वारिंशत्',
    iast: 'ekacatvāriṁśat',
    english: 'Forty-One',
    decadeGroup: '41-50',
    decadeLabelDev: 'पञ्चमावलिः (४१-५०)',
    breakdown: 'एक (1) + चत्वारिंशत् (40)',
  },
  {
    value: 42,
    devanagariNumeral: '४२',
    word: 'द्विचत्वारिंशत्',
    iast: 'dvicatvāriṁśat',
    english: 'Forty-Two',
    decadeGroup: '41-50',
    decadeLabelDev: 'पञ्चमावलिः (४१-५०)',
    breakdown: 'द्वि (2) + चत्वारिंशत् (40)',
    alternatives: ['द्वाचत्वारिंशत्'],
  },
  {
    value: 43,
    devanagariNumeral: '४३',
    word: 'त्रिचत्वारिंशत्',
    iast: 'tricatvāriṁśat',
    english: 'Forty-Three',
    decadeGroup: '41-50',
    decadeLabelDev: 'पञ्चमावलिः (४१-५०)',
    breakdown: 'त्रि (3) + चत्वारिंशत् (40)',
    alternatives: ['त्रयश्चत्वारिंशत्'],
  },
  {
    value: 44,
    devanagariNumeral: '४४',
    word: 'चतुश्चत्वारिंशत्',
    iast: 'catuścatvāriṁśat',
    english: 'Forty-Four',
    decadeGroup: '41-50',
    decadeLabelDev: 'पञ्चमावलिः (४१-५०)',
    breakdown: 'चतुः + चत्वारिंशत् -> चतुश्चत्वारिंशत् (visarga becomes palatal ś before c)',
  },
  {
    value: 45,
    devanagariNumeral: '४५',
    word: 'पञ्चचत्वारिंशत्',
    iast: 'pañcacatvāriṁśat',
    english: 'Forty-Five',
    decadeGroup: '41-50',
    decadeLabelDev: 'पञ्चमावलिः (४१-५०)',
    breakdown: 'पञ्च (5) + चत्वारिंशत् (40)',
  },
  {
    value: 46,
    devanagariNumeral: '४६',
    word: 'षट्चत्वारिंशत्',
    iast: 'ṣaṭcatvāriṁśat',
    english: 'Forty-Six',
    decadeGroup: '41-50',
    decadeLabelDev: 'पञ्चमावलिः (४१-५०)',
    breakdown: 'षट् (6) + चत्वारिंशत् (40)',
  },
  {
    value: 47,
    devanagariNumeral: '४७',
    word: 'सप्तचत्वारिंशत्',
    iast: 'saptacatvāriṁśat',
    english: 'Forty-Seven',
    decadeGroup: '41-50',
    decadeLabelDev: 'पञ्चमावलिः (४१-५०)',
    breakdown: 'सप्त (7) + चत्वारिंशत् (40)',
  },
  {
    value: 48,
    devanagariNumeral: '४८',
    word: 'अष्टचत्वारिंशत्',
    iast: 'aṣṭacatvāriṁśat',
    english: 'Forty-Eight',
    decadeGroup: '41-50',
    decadeLabelDev: 'पञ्चमावलिः (४१-५०)',
    breakdown: 'अष्ट (8) + चत्वारिंशत् (40)',
    alternatives: ['अष्टाचत्वारिंशत्'],
  },
  {
    value: 49,
    devanagariNumeral: '४९',
    word: 'नवचत्वारिंशत्',
    iast: 'navacatvāriṁśat',
    english: 'Forty-Nine',
    decadeGroup: '41-50',
    decadeLabelDev: 'पञ्चमावलिः (४१-५०)',
    breakdown: 'नव (9) + चत्वारिंशत् (40) / एकोनपञ्चाशत् (1 less than 50)',
    alternatives: ['एकोनपञ्चाशत्', 'ऊनपञ्चाशत्'],
  },
  {
    value: 50,
    devanagariNumeral: '५०',
    word: 'पञ्चाशत्',
    iast: 'pañcāśat',
    english: 'Fifty',
    decadeGroup: '41-50',
    decadeLabelDev: 'पञ्चमावलिः (४१-५०)',
    breakdown: 'पञ्चाशत् (Half-century mark, feminine noun)',
  },

  // 51 to 60
  {
    value: 51,
    devanagariNumeral: '५१',
    word: 'एकपञ्चाशत्',
    iast: 'ekapañcāśat',
    english: 'Fifty-One',
    decadeGroup: '51-60',
    decadeLabelDev: 'षष्ठावलिः (५१-६०)',
    breakdown: 'एक (1) + पञ्चाशत् (50)',
  },
  {
    value: 52,
    devanagariNumeral: '५२',
    word: 'द्विपञ्चाशत्',
    iast: 'dvipañcāśat',
    english: 'Fifty-Two',
    decadeGroup: '51-60',
    decadeLabelDev: 'षष्ठावलिः (५१-६०)',
    breakdown: 'द्वि (2) + पञ्चाशत् (50)',
    alternatives: ['द्वापञ्चाशत्'],
  },
  {
    value: 53,
    devanagariNumeral: '५३',
    word: 'त्रिपञ्चाशत्',
    iast: 'tripañcāśat',
    english: 'Fifty-Three',
    decadeGroup: '51-60',
    decadeLabelDev: 'षष्ठावलिः (५१-६०)',
    breakdown: 'त्रि (3) + पञ्चाशत् (50)',
    alternatives: ['त्रयःपञ्चाशत्'],
  },
  {
    value: 54,
    devanagariNumeral: '५४',
    word: 'चतुःपञ्चाशत्',
    iast: 'catuḥpañcāśat',
    english: 'Fifty-Four',
    decadeGroup: '51-60',
    decadeLabelDev: 'षष्ठावलिः (५१-६०)',
    breakdown: 'चतुः (4) + पञ्चाशत् (50)',
    alternatives: ['चतुष्पञ्चाशत्'],
  },
  {
    value: 55,
    devanagariNumeral: '५५',
    word: 'पञ्चपञ्चाशत्',
    iast: 'pañcapañcāśat',
    english: 'Fifty-Five',
    decadeGroup: '51-60',
    decadeLabelDev: 'षष्ठावलिः (५१-६०)',
    breakdown: 'पञ्च (5) + पञ्चाशत् (50)',
  },
  {
    value: 56,
    devanagariNumeral: '५६',
    word: 'षट्पञ्चाशत्',
    iast: 'ṣaṭpañcāśat',
    english: 'Fifty-Six',
    decadeGroup: '51-60',
    decadeLabelDev: 'षष्ठावलिः (५१-६०)',
    breakdown: 'षट् (6) + पञ्चाशत् (50)',
  },
  {
    value: 57,
    devanagariNumeral: '५७',
    word: 'सप्तपञ्चाशत्',
    iast: 'saptapañcāśat',
    english: 'Fifty-Seven',
    decadeGroup: '51-60',
    decadeLabelDev: 'षष्ठावलिः (५१-६०)',
    breakdown: 'सप्त (7) + पञ्चाशत् (50)',
  },
  {
    value: 58,
    devanagariNumeral: '५८',
    word: 'अष्टपञ्चाशत्',
    iast: 'aṣṭapañcāśat',
    english: 'Fifty-Eight',
    decadeGroup: '51-60',
    decadeLabelDev: 'षष्ठावलिः (५१-६०)',
    breakdown: 'अष्ट (8) + पञ्चाशत् (50)',
    alternatives: ['अष्टापञ्चाशत्'],
  },
  {
    value: 59,
    devanagariNumeral: '५९',
    word: 'नवपञ्चाशत्',
    iast: 'navapañcāśat',
    english: 'Fifty-Nine',
    decadeGroup: '51-60',
    decadeLabelDev: 'षष्ठावलिः (५१-६०)',
    breakdown: 'नव (9) + पञ्चाशत् (50) / एकोनषष्टिः (1 less than 60)',
    alternatives: ['एकोनषष्टिः', 'ऊनषष्टिः'],
  },
  {
    value: 60,
    devanagariNumeral: '६०',
    word: 'षष्टिः',
    iast: 'ṣaṣṭiḥ',
    english: 'Sixty',
    decadeGroup: '51-60',
    decadeLabelDev: 'षष्ठावलिः (५१-६०)',
    breakdown: 'षष्टिः (Feminine noun ending in short i with visarga)',
    notes: 'Basis of sexagesimal time division in Indian astronomy (60 Palas, 60 Ghatis).',
  },

  // 61 to 70
  {
    value: 61,
    devanagariNumeral: '६१',
    word: 'एकषष्टिः',
    iast: 'ekaṣaṣṭiḥ',
    english: 'Sixty-One',
    decadeGroup: '61-70',
    decadeLabelDev: 'सप्तमावलिः (६१-७०)',
    breakdown: 'एक (1) + षष्टिः (60)',
  },
  {
    value: 62,
    devanagariNumeral: '६२',
    word: 'द्विषष्टिः',
    iast: 'dviṣaṣṭiḥ',
    english: 'Sixty-Two',
    decadeGroup: '61-70',
    decadeLabelDev: 'सप्तमावलिः (६१-७०)',
    breakdown: 'द्वि (2) + षष्टिः (60)',
    alternatives: ['द्वाषष्टिः'],
  },
  {
    value: 63,
    devanagariNumeral: '६३',
    word: 'त्रिषष्टिः',
    iast: 'triṣaṣṭiḥ',
    english: 'Sixty-Three',
    decadeGroup: '61-70',
    decadeLabelDev: 'सप्तमावलिः (६१-७०)',
    breakdown: 'त्रि (3) + षष्टिः (60)',
    alternatives: ['त्रयःषष्टिः'],
  },
  {
    value: 64,
    devanagariNumeral: '६४',
    word: 'चतुःषष्टिः',
    iast: 'catuḥṣaṣṭiḥ',
    english: 'Sixty-Four',
    decadeGroup: '61-70',
    decadeLabelDev: 'सप्तमावलिः (६१-७०)',
    breakdown: 'चतुः (4) + षष्टिः (60)',
    alternatives: ['चतुष्षष्टिः'],
    notes: 'Famous in the 64 traditional Indian arts (चतुःषष्टि कलाः).',
  },
  {
    value: 65,
    devanagariNumeral: '६५',
    word: 'पञ्चषष्टिः',
    iast: 'pañcaṣaṣṭiḥ',
    english: 'Sixty-Five',
    decadeGroup: '61-70',
    decadeLabelDev: 'सप्तमावलिः (६१-७०)',
    breakdown: 'पञ्च (5) + षष्टिः (60)',
  },
  {
    value: 66,
    devanagariNumeral: '६६',
    word: 'षट्षष्टिः',
    iast: 'ṣaṭṣaṣṭiḥ',
    english: 'Sixty-Six',
    decadeGroup: '61-70',
    decadeLabelDev: 'सप्तमावलिः (६१-७०)',
    breakdown: 'षट् (6) + षष्टिः (60)',
  },
  {
    value: 67,
    devanagariNumeral: '६७',
    word: 'सप्तषष्टिः',
    iast: 'saptaṣaṣṭiḥ',
    english: 'Sixty-Seven',
    decadeGroup: '61-70',
    decadeLabelDev: 'सप्तमावलिः (६१-७०)',
    breakdown: 'सप्त (7) + षष्टिः (60)',
  },
  {
    value: 68,
    devanagariNumeral: '६८',
    word: 'अष्टषष्टिः',
    iast: 'aṣṭaṣaṣṭiḥ',
    english: 'Sixty-Eight',
    decadeGroup: '61-70',
    decadeLabelDev: 'सप्तमावलिः (६१-७०)',
    breakdown: 'अष्ट (8) + षष्टिः (60)',
    alternatives: ['अष्टाषष्टिः'],
  },
  {
    value: 69,
    devanagariNumeral: '६९',
    word: 'नवषष्टिः',
    iast: 'navaṣaṣṭiḥ',
    english: 'Sixty-Nine',
    decadeGroup: '61-70',
    decadeLabelDev: 'सप्तमावलिः (६१-७०)',
    breakdown: 'नव (9) + षष्टिः (60) / एकोनसप्ततिः (1 less than 70)',
    alternatives: ['एकोनसप्ततिः', 'ऊनसप्ततिः'],
  },
  {
    value: 70,
    devanagariNumeral: '७०',
    word: 'सप्ततिः',
    iast: 'saptatiḥ',
    english: 'Seventy',
    decadeGroup: '61-70',
    decadeLabelDev: 'सप्तमावलिः (६१-७०)',
    breakdown: 'सप्ततिः (Feminine noun ending in -तिः)',
  },

  // 71 to 80
  {
    value: 71,
    devanagariNumeral: '७१',
    word: 'एकसप्ततिः',
    iast: 'ekasaptatiḥ',
    english: 'Seventy-One',
    decadeGroup: '71-80',
    decadeLabelDev: 'अष्टमावलिः (७१-८०)',
    breakdown: 'एक (1) + सप्ततिः (70)',
  },
  {
    value: 72,
    devanagariNumeral: '७२',
    word: 'द्विसप्ततिः',
    iast: 'dvisaptatiḥ',
    english: 'Seventy-Two',
    decadeGroup: '71-80',
    decadeLabelDev: 'अष्टमावलिः (७१-८०)',
    breakdown: 'द्वि (2) + सप्ततिः (70)',
    alternatives: ['द्वासप्ततिः'],
  },
  {
    value: 73,
    devanagariNumeral: '७३',
    word: 'त्रिसप्ततिः',
    iast: 'trisaptatiḥ',
    english: 'Seventy-Three',
    decadeGroup: '71-80',
    decadeLabelDev: 'अष्टमावलिः (७१-८०)',
    breakdown: 'त्रि (3) + सप्ततिः (70)',
    alternatives: ['त्रयस्सप्ततिः'],
  },
  {
    value: 74,
    devanagariNumeral: '७४',
    word: 'चतुस्सप्ततिः',
    iast: 'catussaptatiḥ',
    english: 'Seventy-Four',
    decadeGroup: '71-80',
    decadeLabelDev: 'अष्टमावलिः (७१-८०)',
    breakdown: 'चतुः + सप्ततिः -> चतुस्सप्ततिः (visarga assimilates to dental s)',
  },
  {
    value: 75,
    devanagariNumeral: '७५',
    word: 'पञ्चसप्ततिः',
    iast: 'pañcasaptatiḥ',
    english: 'Seventy-Five',
    decadeGroup: '71-80',
    decadeLabelDev: 'अष्टमावलिः (७१-८०)',
    breakdown: 'पञ्च (5) + सप्ततिः (70)',
  },
  {
    value: 76,
    devanagariNumeral: '७६',
    word: 'षट्सप्ततिः',
    iast: 'ṣaṭsaptatiḥ',
    english: 'Seventy-Six',
    decadeGroup: '71-80',
    decadeLabelDev: 'अष्टमावलिः (७१-८०)',
    breakdown: 'षट् (6) + सप्ततिः (70)',
  },
  {
    value: 77,
    devanagariNumeral: '७७',
    word: 'सप्तसप्ततिः',
    iast: 'saptasaptatiḥ',
    english: 'Seventy-Seven',
    decadeGroup: '71-80',
    decadeLabelDev: 'अष्टमावलिः (७१-८०)',
    breakdown: 'सप्त (7) + सप्ततिः (70)',
  },
  {
    value: 78,
    devanagariNumeral: '७८',
    word: 'अष्टसप्ततिः',
    iast: 'aṣṭasaptatiḥ',
    english: 'Seventy-Eight',
    decadeGroup: '71-80',
    decadeLabelDev: 'अष्टमावलिः (७१-८०)',
    breakdown: 'अष्ट (8) + सप्ततिः (70)',
    alternatives: ['अष्टासप्ततिः'],
  },
  {
    value: 79,
    devanagariNumeral: '७९',
    word: 'नवसप्ततिः',
    iast: 'navasaptatiḥ',
    english: 'Seventy-Nine',
    decadeGroup: '71-80',
    decadeLabelDev: 'अष्टमावलिः (७१-८०)',
    breakdown: 'नव (9) + सप्ततिः (70) / एकोनाशीतिः (1 less than 80)',
    alternatives: ['एकोनाशीतिः', 'ऊनाशीतिः'],
  },
  {
    value: 80,
    devanagariNumeral: '८०',
    word: 'अशीतिः',
    iast: 'aśītiḥ',
    english: 'Eighty',
    decadeGroup: '71-80',
    decadeLabelDev: 'अष्टमावलिः (७१-८०)',
    breakdown: 'अशीतिः (Feminine noun)',
    notes: 'Begins with vowel a-, causing unique Sandhi mergers in the 80s decade.',
  },

  // 81 to 90
  {
    value: 81,
    devanagariNumeral: '८१',
    word: 'एकाशीतिः',
    iast: 'ekāśītiḥ',
    english: 'Eighty-One',
    decadeGroup: '81-90',
    decadeLabelDev: 'नवमावलिः (८१-९०)',
    breakdown: 'एक + अशीतिः -> एकाशीतिः (Savarna Dirgha Sandhi a + a = ā)',
  },
  {
    value: 82,
    devanagariNumeral: '८२',
    word: 'द्व्यशीतिः',
    iast: 'dvyaśītiḥ',
    english: 'Eighty-Two',
    decadeGroup: '81-90',
    decadeLabelDev: 'नवमावलिः (८१-९०)',
    breakdown: 'द्वि + अशीतिः -> द्व्यशीतिः (Yan Sandhi i + a = ya)',
    notes: 'Yan Sandhi turns i into semi-vowel y!',
  },
  {
    value: 83,
    devanagariNumeral: '८३',
    word: 'त्र्यशीतिः',
    iast: 'tryaśītiḥ',
    english: 'Eighty-Three',
    decadeGroup: '81-90',
    decadeLabelDev: 'नवमावलिः (८१-९०)',
    breakdown: 'त्रि + अशीतिः -> त्र्यशीतिः (Yan Sandhi i + a = ya)',
  },
  {
    value: 84,
    devanagariNumeral: '८४',
    word: 'चतुरशीतिः',
    iast: 'caturaśītiḥ',
    english: 'Eighty-Four',
    decadeGroup: '81-90',
    decadeLabelDev: 'नवमावलिः (८१-९०)',
    breakdown: 'चतुर् + अशीतिः -> चतुरशीतिः (r merges smoothly with a)',
    notes: '840,000 species of living beings (चतुरशीतिलक्ष योनि) in classical literature.',
  },
  {
    value: 85,
    devanagariNumeral: '८५',
    word: 'पञ्चाशीतिः',
    iast: 'pañcāśītiḥ',
    english: 'Eighty-Five',
    decadeGroup: '81-90',
    decadeLabelDev: 'नवमावलिः (८१-९०)',
    breakdown: 'पञ्च + अशीतिः -> पञ्चाशीतिः (Dirgha Sandhi a + a = ā)',
  },
  {
    value: 86,
    devanagariNumeral: '८६',
    word: 'षडशीतिः',
    iast: 'ṣaḍaśītiḥ',
    english: 'Eighty-Six',
    decadeGroup: '81-90',
    decadeLabelDev: 'नवमावलिः (८१-९०)',
    breakdown: 'षट् + अशीतिः -> षडशीतिः (ṭ becomes voiced ḍ before vowel)',
  },
  {
    value: 87,
    devanagariNumeral: '८७',
    word: 'सप्ताशीतिः',
    iast: 'saptāśītiḥ',
    english: 'Eighty-Seven',
    decadeGroup: '81-90',
    decadeLabelDev: 'नवमावलिः (८१-९०)',
    breakdown: 'सप्त + अशीतिः -> सप्ताशीतिः (Dirgha Sandhi a + a = ā)',
  },
  {
    value: 88,
    devanagariNumeral: '८८',
    word: 'अष्टाशीतिः',
    iast: 'aṣṭāśītiḥ',
    english: 'Eighty-Eight',
    decadeGroup: '81-90',
    decadeLabelDev: 'नवमावलिः (८१-९०)',
    breakdown: 'अष्ट + अशीतिः -> अष्टाशीतिः (Dirgha Sandhi a + a = ā)',
  },
  {
    value: 89,
    devanagariNumeral: '८९',
    word: 'नवाशीतिः',
    iast: 'navāśītiḥ',
    english: 'Eighty-Nine',
    decadeGroup: '81-90',
    decadeLabelDev: 'नवमावलिः (८१-९०)',
    breakdown: 'नव + अशीतिः -> नवाशीतिः / एकोननवतिः (1 less than 90)',
    alternatives: ['एकोननवतिः', 'ऊननवतिः'],
  },
  {
    value: 90,
    devanagariNumeral: '९०',
    word: 'नवतिः',
    iast: 'navatiḥ',
    english: 'Ninety',
    decadeGroup: '81-90',
    decadeLabelDev: 'नवमावलिः (८१-९०)',
    breakdown: 'नवतिः (Feminine noun)',
  },

  // 91 to 100
  {
    value: 91,
    devanagariNumeral: '९१',
    word: 'एकनवतिः',
    iast: 'ekanavatiḥ',
    english: 'Ninety-One',
    decadeGroup: '91-100',
    decadeLabelDev: 'दशमावलिः (९१-१००)',
    breakdown: 'एक (1) + नवतिः (90)',
  },
  {
    value: 92,
    devanagariNumeral: '९२',
    word: 'द्विनवतिः',
    iast: 'dvinavatiḥ',
    english: 'Ninety-Two',
    decadeGroup: '91-100',
    decadeLabelDev: 'दशमावलिः (९१-१००)',
    breakdown: 'द्वि (2) + नवतिः (90)',
    alternatives: ['द्वानवतिः'],
  },
  {
    value: 93,
    devanagariNumeral: '९३',
    word: 'त्रिनवतिः',
    iast: 'trinavatiḥ',
    english: 'Ninety-Three',
    decadeGroup: '91-100',
    decadeLabelDev: 'दशमावलिः (९१-१००)',
    breakdown: 'त्रि (3) + नवतिः (90)',
    alternatives: ['त्रयोनवतिः'],
  },
  {
    value: 94,
    devanagariNumeral: '९४',
    word: 'चतुर्नवतिः',
    iast: 'caturnavatiḥ',
    english: 'Ninety-Four',
    decadeGroup: '91-100',
    decadeLabelDev: 'दशमावलिः (९१-१००)',
    breakdown: 'चतुर् (4) + नवतिः (90)',
  },
  {
    value: 95,
    devanagariNumeral: '९५',
    word: 'पञ्चनवतिः',
    iast: 'pañcanavatiḥ',
    english: 'Ninety-Five',
    decadeGroup: '91-100',
    decadeLabelDev: 'दशमावलिः (९१-१००)',
    breakdown: 'पञ्च (5) + नवतिः (90)',
  },
  {
    value: 96,
    devanagariNumeral: '९६',
    word: 'षण्णवतिः',
    iast: 'ṣaṇṇavatiḥ',
    english: 'Ninety-Six',
    decadeGroup: '91-100',
    decadeLabelDev: 'दशमावलिः (९१-१००)',
    breakdown: 'षट् + नवतिः -> षण्णवतिः (Anunasika Sandhi ṭ becomes retroflex nasal ṇ before n)',
    notes: 'Crucial Sandhi example frequently tested in CBSE/NCERT and Sanskrit grammar exams!',
  },
  {
    value: 97,
    devanagariNumeral: '९७',
    word: 'सप्तनवतिः',
    iast: 'saptanavatiḥ',
    english: 'Ninety-Seven',
    decadeGroup: '91-100',
    decadeLabelDev: 'दशमावलिः (९१-१००)',
    breakdown: 'सप्त (7) + नवतिः (90)',
  },
  {
    value: 98,
    devanagariNumeral: '९८',
    word: 'अष्टनवतिः',
    iast: 'aṣṭanavatiḥ',
    english: 'Ninety-Eight',
    decadeGroup: '91-100',
    decadeLabelDev: 'दशमावलिः (९१-१००)',
    breakdown: 'अष्ट (8) + नवतिः (90)',
    alternatives: ['अष्टानवतिः'],
  },
  {
    value: 99,
    devanagariNumeral: '९९',
    word: 'नवनवतिः',
    iast: 'navanavatiḥ',
    english: 'Ninety-Nine',
    decadeGroup: '91-100',
    decadeLabelDev: 'दशमावलिः (९१-१००)',
    breakdown: 'नव (9) + नवतिः (90) / एकोनशतम् (1 less than 100)',
    alternatives: ['एकोनशतम्', 'ऊनशतम्'],
  },
  {
    value: 100,
    devanagariNumeral: '१००',
    word: 'शतम्',
    iast: 'śatam',
    english: 'One Hundred',
    decadeGroup: '91-100',
    decadeLabelDev: 'दशमावलिः (९१-१००)',
    breakdown: 'शतम् (Neuter singular noun, cognate with Latin centum)',
    notes: 'Forms the milestone for higher orders of magnitude. 100 is grammatically neuter singular.',
  },
];

// Gender Declension Details for 1 to 4
export const GENDER_DECLENSIONS_1_TO_4: GenderNumberExample[] = [
  {
    number: 1,
    devanagariNumeral: '१',
    stem: 'एक (eka)',
    masculine: {
      form: 'एकः',
      iast: 'ekaḥ',
      example: 'एकः बालकः पठति।',
      exampleEn: 'One boy reads.',
    },
    feminine: {
      form: 'एका',
      iast: 'ekā',
      example: 'एका बालिका क्रीडति।',
      exampleEn: 'One girl plays.',
    },
    neuter: {
      form: 'एकम्',
      iast: 'ekam',
      example: 'एकम् फलम् पतति।',
      exampleEn: 'One fruit falls.',
    },
    grammaticalRule: 'Declines like an adjective strictly in Singular (एकवचनम्). Matches the gender, case, and number of its qualifying noun.',
  },
  {
    number: 2,
    devanagariNumeral: '२',
    stem: 'द्वि (dvi)',
    masculine: {
      form: 'द्वौ',
      iast: 'dvau',
      example: 'द्वौ छात्रौ लिखतः।',
      exampleEn: 'Two male students write.',
    },
    feminine: {
      form: 'द्वे',
      iast: 'dve',
      example: 'द्वे बालिके हसतः।',
      exampleEn: 'Two girls laugh.',
    },
    neuter: {
      form: 'द्वे',
      iast: 'dve',
      example: 'द्वे नेत्रे पश्यतः।',
      exampleEn: 'Two eyes see.',
    },
    grammaticalRule: 'Always in Dual (द्विवचनम्). Note that feminine and neuter nominative forms are identical (द्वे).',
  },
  {
    number: 3,
    devanagariNumeral: '३',
    stem: 'त्रि (tri)',
    masculine: {
      form: 'त्रयः',
      iast: 'trayaḥ',
      example: 'त्रयः मुनयः वसन्ति।',
      exampleEn: 'Three sages reside.',
    },
    feminine: {
      form: 'तिस्रः',
      iast: 'tisraḥ',
      example: 'तिस्रः नद्यः वहन्ति।',
      exampleEn: 'Three rivers flow.',
    },
    neuter: {
      form: 'त्रीणि',
      iast: 'trīṇi',
      example: 'त्रीणि पुस्तकानि सन्ति।',
      exampleEn: 'There are three books.',
    },
    grammaticalRule: 'Always in Plural (बहुवचनम्). Note the unique feminine stem substitution tisṛ- yielding तिस्रः.',
  },
  {
    number: 4,
    devanagariNumeral: '४',
    stem: 'चतुर् (catur)',
    masculine: {
      form: 'चत्वारः',
      iast: 'catvāraḥ',
      example: 'चत्वारः वेदाः सन्ति।',
      exampleEn: 'There are four Vedas.',
    },
    feminine: {
      form: 'चतस्रः',
      iast: 'catasraḥ',
      example: 'चतस्रः धेनवः चरन्ति।',
      exampleEn: 'Four cows graze.',
    },
    neuter: {
      form: 'चत्वारि',
      iast: 'catvāri',
      example: 'चत्वारि पुष्पाणि विकसन्ति।',
      exampleEn: 'Four flowers bloom.',
    },
    grammaticalRule: 'Always in Plural (बहुवचनम्). Feminine stem becomes catasṛ- yielding चतस्रः.',
  },
];

// Ordinal Numbers (पूरण-संख्याः)
export const ORDINAL_NUMBERS_LIST: OrdinalNumberItem[] = [
  {
    rank: 1,
    numeral: '१',
    masculine: 'प्रथमः',
    feminine: 'प्रथमा',
    neuter: 'प्रथमम्',
    iast: 'prathamaḥ / prathamā / prathamam',
    english: '1st (First)',
    example: 'प्रथमः पाठः (First lesson)',
  },
  {
    rank: 2,
    numeral: '२',
    masculine: 'द्वितीयः',
    feminine: 'द्वितीया',
    neuter: 'द्वितीयम्',
    iast: 'dvitīyaḥ / dvitīyā / dvitīyam',
    english: '2nd (Second)',
    example: 'द्वितीयः श्लोकः (Second verse)',
  },
  {
    rank: 3,
    numeral: '३',
    masculine: 'तृतीयः',
    feminine: 'तृतीया',
    neuter: 'तृतीयम्',
    iast: 'tṛtīyaḥ / tṛtīyā / tṛtīyam',
    english: '3rd (Third)',
    example: 'तृतीया कक्षा (Third class)',
  },
  {
    rank: 4,
    numeral: '४',
    masculine: 'चतुर्थः',
    feminine: 'चतुर्थी',
    neuter: 'चतुर्थम्',
    iast: 'caturthaḥ / caturthī / caturtham',
    english: '4th (Fourth)',
    example: 'चतुर्थं सोपानम् (Fourth step)',
  },
  {
    rank: 5,
    numeral: '५',
    masculine: 'पञ्चमः',
    feminine: 'पञ्चमी',
    neuter: 'पञ्चमम्',
    iast: 'pañcamaḥ / pañcamī / pañcamam',
    english: '5th (Fifth)',
    example: 'पञ्चमी तिथिः (Fifth lunar day)',
  },
  {
    rank: 6,
    numeral: '६',
    masculine: 'षष्ठः',
    feminine: 'षष्ठी',
    neuter: 'षष्ठम्',
    iast: 'ṣaṣṭhaḥ / ṣaṣṭhī / ṣaṣṭham',
    english: '6th (Sixth)',
    example: 'षष्ठी विभक्तिः (Sixth case / genitive)',
  },
  {
    rank: 7,
    numeral: '७',
    masculine: 'सप्तमः',
    feminine: 'सप्तमी',
    neuter: 'सप्तमम्',
    iast: 'saptamaḥ / saptamī / saptamam',
    english: '7th (Seventh)',
    example: 'सप्तमी विभक्तिः (Locative case)',
  },
  {
    rank: 8,
    numeral: '८',
    masculine: 'अष्टमः',
    feminine: 'अष्टमी',
    neuter: 'अष्टमम्',
    iast: 'aṣṭamaḥ / aṣṭamī / aṣṭamam',
    english: '8th (Eighth)',
    example: 'अष्टमी रात्रिः (Eighth night / Janmashtami)',
  },
  {
    rank: 9,
    numeral: '९',
    masculine: 'नवमः',
    feminine: 'नवमी',
    neuter: 'नवमम्',
    iast: 'navamaḥ / navamī / navamam',
    english: '9th (Ninth)',
    example: 'नवमी तिथिः (Ninth day / Ram Navami)',
  },
  {
    rank: 10,
    numeral: '१०',
    masculine: 'दशमः',
    feminine: 'दशमी',
    neuter: 'दशमम्',
    iast: 'daśamaḥ / daśamī / daśamam',
    english: '10th (Tenth)',
    example: 'दशमी तिथिः (Vijayadashami)',
  },
  {
    rank: 11,
    numeral: '११',
    masculine: 'एकादशः',
    feminine: 'एकादशी',
    neuter: 'एकादशम्',
    iast: 'ekādaśaḥ / ekādaśī / ekādaśam',
    english: '11th (Eleventh)',
    example: 'एकादशी व्रतम् (Eleventh lunar day fast)',
  },
  {
    rank: 12,
    numeral: '१२',
    masculine: 'द्वादशः',
    feminine: 'द्वादशी',
    neuter: 'द्वादशम्',
    iast: 'dvādaśaḥ / dvādaśī / dvādaśam',
    english: '12th (Twelfth)',
    example: 'द्वादशः अध्यायः (Twelfth chapter)',
  },
  {
    rank: 15,
    numeral: '१५',
    masculine: 'पञ्चदशः',
    feminine: 'पञ्चदशी',
    neuter: 'पञ्चदशम्',
    iast: 'pañcadaśaḥ / pañcadaśī / pañcadaśam',
    english: '15th (Fifteenth)',
    example: 'पञ्चदशः दिवसः (Fifteenth day)',
  },
  {
    rank: 20,
    numeral: '२०',
    masculine: 'विंशतितमः / विंशः',
    feminine: 'विंशतितमी / विंशी',
    neuter: 'विंशतितमम्',
    iast: 'viṁśatitamaḥ / viṁśatitamī',
    english: '20th (Twentieth)',
    example: 'विंशतितमं वर्षम् (Twentieth year)',
  },
  {
    rank: 50,
    numeral: '५०',
    masculine: 'पञ्चाशत्तमः',
    feminine: 'पञ्चाशत्तमी',
    neuter: 'पञ्चाशत्तमम्',
    iast: 'pañcāśattamaḥ / pañcāśattamī',
    english: '50th (Fiftieth)',
    example: 'पञ्चाशत्तमः उत्सवः (Golden jubilee / 50th festival)',
  },
  {
    rank: 100,
    numeral: '१००',
    masculine: 'शततमः',
    feminine: 'शततमी',
    neuter: 'शततमम्',
    iast: 'śatatamaḥ / śatatamī / śatatamam',
    english: '100th (Hundredth)',
    example: 'शततमः दिवसः (Centenary / 100th day)',
  },
];

// Vedic and Classical Powers of 10 Scale (महा-संख्या-परिमाणम्)
export const VEDIC_LARGE_NUMBERS: VedicLargeNumberItem[] = [
  {
    exponent: 0,
    powerOfTen: '10⁰',
    numericDisplay: '1',
    sanskritName: 'एकम्',
    iast: 'ekam',
    englishMeaning: 'One (Unit)',
    source: 'Yajurveda (यजुर्वेदः १७.२)',
    description: 'The fundamental indivisible point and origin of numbering.',
  },
  {
    exponent: 1,
    powerOfTen: '10¹',
    numericDisplay: '10',
    sanskritName: 'दश',
    iast: 'daśa',
    englishMeaning: 'Ten',
    source: 'Yajurveda Saṁhitā',
    description: 'The base scale of the Indian decimal positional place-value architecture.',
  },
  {
    exponent: 2,
    powerOfTen: '10²',
    numericDisplay: '100',
    sanskritName: 'शतम्',
    iast: 'śatam',
    englishMeaning: 'Hundred',
    source: 'Yajurveda Saṁhitā',
    description: 'One hundred. The boundary where classical 1-100 foundational counting culminates.',
  },
  {
    exponent: 3,
    powerOfTen: '10³',
    numericDisplay: '1,000',
    sanskritName: 'सहस्रम्',
    iast: 'sahasram',
    englishMeaning: 'Thousand',
    source: 'Purusha Sukta (सहस्रशीर्षा पुरुषः)',
    description: 'One thousand. Often poetically signifies boundless completeness.',
  },
  {
    exponent: 4,
    powerOfTen: '10⁴',
    numericDisplay: '10,000',
    sanskritName: 'अयुतम्',
    iast: 'ayutam',
    englishMeaning: 'Ten Thousand',
    source: 'Taittirīya Saṁhitā',
    description: 'Myriad / Ten thousand. Literally "unjoined" or self-standing decade power.',
  },
  {
    exponent: 5,
    powerOfTen: '10⁵',
    numericDisplay: '1,00,000',
    sanskritName: 'लक्षम् (नियुतम्)',
    iast: 'lakṣam / niyutam',
    englishMeaning: 'One Lakh (Hundred Thousand)',
    source: 'Rāmāyaṇa & Classical Ganita',
    description: 'One hundred thousand. The bedrock of traditional Indian financial notation.',
  },
  {
    exponent: 6,
    powerOfTen: '10⁶',
    numericDisplay: '10,00,000',
    sanskritName: 'प्रयुतम्',
    iast: 'prayutam',
    englishMeaning: 'One Million (Ten Lakhs)',
    source: 'Yajurveda Saṁhitā',
    description: 'Ten hundred thousand = 1,000,000.',
  },
  {
    exponent: 7,
    powerOfTen: '10⁷',
    numericDisplay: '1,00,00,000',
    sanskritName: 'कोटिः',
    iast: 'koṭiḥ',
    englishMeaning: 'One Crore (Ten Million)',
    source: 'Vālmīki Rāmāyaṇa / Purāṇa',
    description: '10,000,000. Revered in literature; "त्रयस्त्रिंशत् कोटि देवाः" represents divine supreme classes.',
  },
  {
    exponent: 8,
    powerOfTen: '10⁸',
    numericDisplay: '10,00,00,000',
    sanskritName: 'अर्बुदम्',
    iast: 'arbudam',
    englishMeaning: 'Hundred Million (Ten Crores)',
    source: 'Vālmīki Rāmāyaṇa (Yuddha Kāṇḍa 28.35)',
    description: 'Ten crores = 100,000,000.',
  },
  {
    exponent: 9,
    powerOfTen: '10⁹',
    numericDisplay: '1,00,00,00,000',
    sanskritName: 'अब्जम् / पद्मम्',
    iast: 'abjam / padmam',
    englishMeaning: 'One Billion (One Arab / 100 Crores)',
    source: 'Āryabhaṭīya / Līlāvatī',
    description: 'One thousand million = 1,000,000,000.',
  },
  {
    exponent: 10,
    powerOfTen: '10¹⁰',
    numericDisplay: '10,00,00,00,000',
    sanskritName: 'खर्वम्',
    iast: 'kharvam',
    englishMeaning: 'Ten Billion',
    source: 'Bhāskarācārya (Līlāvatī 2)',
    description: 'Ten billion = 10,000,000,000.',
  },
  {
    exponent: 11,
    powerOfTen: '10¹¹',
    numericDisplay: '1,00,00,00,00,000',
    sanskritName: 'निखर्वम्',
    iast: 'nikharvam',
    englishMeaning: 'Hundred Billion',
    source: 'Līlāvatī / Rāmāyaṇa',
    description: 'One hundred billion.',
  },
  {
    exponent: 12,
    powerOfTen: '10¹²',
    numericDisplay: '10,00,00,00,00,000',
    sanskritName: 'महापद्मम्',
    iast: 'mahāpadmam',
    englishMeaning: 'One Trillion',
    source: 'Bhāskarācārya',
    description: 'One trillion = 1,000,000,000,000.',
  },
  {
    exponent: 13,
    powerOfTen: '10¹³',
    numericDisplay: '1,00,00,00,00,00,000',
    sanskritName: 'शङ्कुः (शङ्खः)',
    iast: 'śaṅkuḥ / śaṅkhaḥ',
    englishMeaning: 'Ten Trillion',
    source: 'Vālmīki Rāmāyaṇa',
    description: 'Ten trillion. Described in Sugriva\'s vanara army enumeration.',
  },
  {
    exponent: 14,
    powerOfTen: '10¹⁴',
    numericDisplay: '10,00,00,00,00,00,000',
    sanskritName: 'जलधिः (समुद्रः)',
    iast: 'jaladhiḥ / samudraḥ',
    englishMeaning: 'Hundred Trillion',
    source: 'Līlāvatī / Veda',
    description: 'Poetically styled after the immense vastness of the ocean.',
  },
  {
    exponent: 15,
    powerOfTen: '10¹⁵',
    numericDisplay: '1,00,00,00,00,00,00,000',
    sanskritName: 'अन्त्यम्',
    iast: 'antyam',
    englishMeaning: 'One Quadrillion',
    source: 'Līlāvatī (Līlāvatī Verse 2)',
    description: '1,000,000,000,000,000.',
  },
  {
    exponent: 16,
    powerOfTen: '10¹⁶',
    numericDisplay: '10,00,00,00,00,00,00,000',
    sanskritName: 'मध्यम्',
    iast: 'madhyam',
    englishMeaning: 'Ten Quadrillion',
    source: 'Yajurveda Saṁhitā',
    description: 'Ten quadrillion.',
  },
  {
    exponent: 17,
    powerOfTen: '10¹⁷',
    numericDisplay: '1,00,00,00,00,00,00,00,000',
    sanskritName: 'परार्धम्',
    iast: 'parārdham',
    englishMeaning: 'One Hundred Quadrillion / Quintillion',
    source: 'Yajurveda & Viṣṇu Purāṇa',
    description: 'Half of the supreme span (Para) — cosmological timespan in Brahma\'s cosmic life cycle.',
  },
];

// Decades Metadata for Filter Navigation
export const DECADE_GROUPS = [
  { id: 'all', label: 'समग्रम् (All 1-100)', range: [1, 100] },
  { id: '1-10', label: '१-१० (प्रथमावलिः)', range: [1, 10] },
  { id: '11-20', label: '११-२० (द्वितीयावलिः)', range: [11, 20] },
  { id: '21-30', label: '२१-३० (तृतीयावलिः)', range: [21, 30] },
  { id: '31-40', label: '३१-४० (चतुर्थावलिः)', range: [31, 40] },
  { id: '41-50', label: '४१-५० (पञ्चमावलिः)', range: [41, 50] },
  { id: '51-60', label: '५१-६० (षष्ठावलिः)', range: [51, 60] },
  { id: '61-70', label: '६१-७० (सप्तमावलिः)', range: [61, 70] },
  { id: '71-80', label: '७१-८० (अष्टमावलिः)', range: [71, 80] },
  { id: '81-90', label: '८१-९० (नवमावलिः)', range: [81, 90] },
  { id: '91-100', label: '९१-१०० (दशमावलिः)', range: [91, 100] },
];

/**
 * Traditional Bhūta-saṅkhyā / Natural & Bodily Number Mnemonics (प्राकृतिकाः संख्याः)
 * Teaching Sanskrit numbers 1 to 10 through universal symbols of the cosmos,
 * the human body, sacred knowledge, and nature (Earth, Eyes, Senses, Digits, etc.)
 */
export interface NumberMnemonicItem {
  value: number;
  devanagariNumeral: string;
  word: string;
  iast: string;
  symbolDev: string;
  symbolIast: string;
  symbolEnglish: string;
  emoji: string;
  theme: 'Cosmic & Nature' | 'Human Body & Senses' | 'Sacred Knowledge';
  genderNote: string;
  alternateDev: string;
  alternateEnglish: string;
  details: string;
  shlokaLine: string;
  tags: string[];
}

export const NUMBER_MNEMONICS_1_TO_10: NumberMnemonicItem[] = [
  {
    value: 1,
    devanagariNumeral: '१',
    word: 'एकम्',
    iast: 'ekam',
    symbolDev: 'एका पृथिवी',
    symbolIast: 'ekā pṛthivī',
    symbolEnglish: 'One Earth',
    emoji: '🌍',
    theme: 'Cosmic & Nature',
    genderNote: 'स्त्रीलिङ्गम् (Feminine): एका पृथिवी (One Earth). In masculine: एकः सूर्यः (One Sun); neuter: एकम् आननम् (One Face).',
    alternateDev: 'एकः सूर्यः',
    alternateEnglish: 'One Sun (Single source of universal radiance)',
    details: 'There is only one Earth (पृथिवी माता) sustaining all living beings, just as there is one radiant Sun (सूर्यः) illuminating the universe and one Supreme Truth (एकं सद् विप्रा बहुधा वदन्ति).',
    shlokaLine: 'एकं वदतु सर्वम् — एका पृथिवी (Speak one: One Earth)',
    tags: ['earth', 'sun', 'one', 'number 1', 'prithvi', 'brahman'],
  },
  {
    value: 2,
    devanagariNumeral: '२',
    word: 'द्वे',
    iast: 'dve',
    symbolDev: 'द्वे नेत्रे',
    symbolIast: 'dve netre',
    symbolEnglish: 'Two Eyes',
    emoji: '👀',
    theme: 'Human Body & Senses',
    genderNote: 'नपुंसकलिङ्गम् द्विवचनम् (Neuter Dual): द्वे नेत्रे (Two Eyes). Masculine dual: द्वौ हस्तौ (Two Hands), द्वौ कर्णौ (Two Ears); feminine: द्वे नासिके.',
    alternateDev: 'द्वौ हस्तौ',
    alternateEnglish: 'Two Hands (Instruments of Karma and Seva)',
    details: 'Two eyes (द्वे नेत्रे) grant stereoscopic vision and insight; two hands (द्वौ हस्तौ) perform right action and compassionate service; two wings enable the bird of spirit to soar.',
    shlokaLine: 'द्वे वदतु सर्वम् — द्वे नेत्रे (Speak two: Two Eyes)',
    tags: ['eyes', 'hands', 'vision', 'two', 'netre', 'dual'],
  },
  {
    value: 3,
    devanagariNumeral: '३',
    word: 'त्रीणि',
    iast: 'trīṇi',
    symbolDev: 'त्रयः लोकाः',
    symbolIast: 'trayaḥ lokāḥ',
    symbolEnglish: 'Three Worlds (Triloka)',
    emoji: '🌌',
    theme: 'Cosmic & Nature',
    genderNote: 'पुंलिङ्गम् (Masculine Plural): त्रयः लोकाः (Three Worlds) / त्रयः कालाः (Three Times). Neuter: त्रीणि भुवनानि / त्रीणि नेत्राणि (Three Eyes of Shiva). Feminine: तिस्रः नद्यः.',
    alternateDev: 'त्रयः कालाः',
    alternateEnglish: 'Three Times (Past, Present, and Future)',
    details: 'The three cosmic realms (भूः Earth, भुवः Atmosphere, स्वः Heavens), the threefold stream of time (भूत Past, वर्तमान Present, भविष्यत् Future), and the threefold nature (सत्त्व-रजस्-तमस्).',
    shlokaLine: 'त्रीणि वदतु सर्वम् — त्रयः लोकाः (Speak three: Three Worlds)',
    tags: ['worlds', 'triloka', 'time', 'kala', 'three', 'trini'],
  },
  {
    value: 4,
    devanagariNumeral: '४',
    word: 'चत्वारि',
    iast: 'catvāri',
    symbolDev: 'चत्वारः वेदाः',
    symbolIast: 'catvāraḥ vedāḥ',
    symbolEnglish: 'Four Vedas',
    emoji: '📜',
    theme: 'Sacred Knowledge',
    genderNote: 'पुंलिङ्गम् (Masculine Plural): चत्वारः वेदाः (Four Vedas) / चत्वारः पादाः (Four Legs). Feminine: चतस्रः दिशः (Four Cardinal Directions). Neuter: चत्वारि फलानि.',
    alternateDev: 'चतस्रः दिशः',
    alternateEnglish: 'Four Cardinal Directions (East, South, West, North)',
    details: 'The four eternal fountains of Vedic wisdom (ऋग्, यजुस्, साम, अथर्वन्), the four cardinal compass directions (दिशः), and the four noble life aspirations (धर्म, अर्थ, काम, मोक्ष).',
    shlokaLine: 'चत्वारि वदतु सर्वम् — चत्वारः वेदाः (Speak four: Four Vedas)',
    tags: ['vedas', 'directions', 'knowledge', 'four', 'catvari'],
  },
  {
    value: 5,
    devanagariNumeral: '५',
    word: 'पञ्च',
    iast: 'pañca',
    symbolDev: 'पञ्च ज्ञानेन्द्रियाणि',
    symbolIast: 'pañca jñānendriyāṇi',
    symbolEnglish: 'Five Sense Organs',
    emoji: '👁️',
    theme: 'Human Body & Senses',
    genderNote: 'त्रिषु लिङ्गेषु समानम् (Uniform in all genders from 5 onward): पञ्च ज्ञानेन्द्रियाणि (N) / पञ्च भूतानि (N) / पञ्च पाण्डवाः (M) / पञ्च अङ्गुल्यः (F).',
    alternateDev: 'पञ्च महाभूतानि',
    alternateEnglish: 'Five Great Elements (Earth, Water, Fire, Air, Space)',
    details: 'The five doorways of human perception: चक्षुः (Eyes/Sight), श्रोत्रम् (Ears/Sound), घ्राणम् (Nose/Smell), रसना (Tongue/Taste), and त्वक् (Skin/Touch); reflecting the five cosmic elements (पृथिवी, आपः, तेजस्, वायुः, आकाशः).',
    shlokaLine: 'पञ्च वदतु सर्वम् — पञ्च ज्ञानेन्द्रियाणि (Speak five: Five Senses)',
    tags: ['senses', 'indriyas', 'elements', 'pancha', 'five'],
  },
  {
    value: 6,
    devanagariNumeral: '६',
    word: 'षट्',
    iast: 'ṣaṭ',
    symbolDev: 'षट् ऋतवः',
    symbolIast: 'ṣaṭ ṛtavaḥ',
    symbolEnglish: 'Six Seasons (Ṛtus)',
    emoji: '🌺',
    theme: 'Cosmic & Nature',
    genderNote: 'त्रिषु लिङ्गेषु समानम्: षट् ऋतवः (M) / षट् रसाः (M) / षट् दर्शनानि (N). Sandhi note: षट् + ऋतवः → षड् ऋतवः.',
    alternateDev: 'षट् रसाः',
    alternateEnglish: 'Six Tastes of Ayurveda (Sweet, Sour, Salty, Pungent, Bitter, Astringent)',
    details: 'The six poetic Indian seasons: वसन्तः (Spring), ग्रीष्मः (Summer), वर्षा (Monsoon), शरद् (Autumn), हेमन्तः (Pre-winter), शिशिरः (Winter); mirrored in the six medicinal rasas (मधुर, अम्ल, लवण, कटु, तिक्त, कषाय).',
    shlokaLine: 'षट् वदतु सर्वम् — षट् ऋतवः (Speak six: Six Seasons)',
    tags: ['seasons', 'ritus', 'tastes', 'rasa', 'six', 'shat'],
  },
  {
    value: 7,
    devanagariNumeral: '७',
    word: 'सप्त',
    iast: 'sapta',
    symbolDev: 'सप्त स्वराः',
    symbolIast: 'sapta svarāḥ',
    symbolEnglish: 'Seven Musical Notes (Saptasvara)',
    emoji: '🎵',
    theme: 'Sacred Knowledge',
    genderNote: 'त्रिषु लिङ्गेषु समानम्: सप्त स्वराः (M) / सप्तर्षयः (M) / सप्त समुद्राः (M) / सप्त वर्णाः (M) / सप्त वासराः (7 Days).',
    alternateDev: 'सप्तर्षयः',
    alternateEnglish: 'Seven Celestial Sages (Ursa Major / Saptarṣi constellation)',
    details: 'The seven foundational musical notes of Gandharva-veda: षड्ज (Sa), ऋषभ (Re), गान्धार (Ga), मध्यम (Ma), पञ्चम (Pa), धैवत (Dha), निषाद (Ni); also honored as the seven immortal Rishis and the seven rays of the sun.',
    shlokaLine: 'सप्त वदतु सर्वम् — सप्त स्वराः (Speak seven: Seven Musical Notes)',
    tags: ['music', 'notes', 'svara', 'rishis', 'seven', 'sapta'],
  },
  {
    value: 8,
    devanagariNumeral: '८',
    word: 'अष्ट',
    iast: 'aṣṭa',
    symbolDev: 'अष्टौ दिशः',
    symbolIast: 'aṣṭau diśaḥ',
    symbolEnglish: 'Eight Directions (Compass Points)',
    emoji: '🧭',
    theme: 'Cosmic & Nature',
    genderNote: 'त्रिषु लिङ्गेषु समानम् (Both अष्ट and अष्टौ are classical Vedic/Paninian variants): अष्टौ दिशः (F) / अष्ट सिद्धयः (F) / अष्टाङ्गयोगः (M).',
    alternateDev: 'अष्ट सिद्धयः',
    alternateEnglish: 'Eight Mystic Attainments (Aṣṭa Siddhis: Aṇimā, Mahimā...)',
    details: 'The eight compass directions guarded by the Ashta-Dikpalas (East, SE, South, SW, West, NW, North, NE); and Patanjali’s eight limbs of Yoga (यम, नियम, आसन, प्राणायाम, प्रत्याहार, धारणा, ध्यान, समाधि).',
    shlokaLine: 'अष्ट वदतु सर्वम् — अष्टौ दिशः (Speak eight: Eight Directions)',
    tags: ['directions', 'ashta', 'siddhi', 'yoga', 'eight', 'ashtau'],
  },
  {
    value: 9,
    devanagariNumeral: '९',
    word: 'नव',
    iast: 'nava',
    symbolDev: 'नव ग्रहाः',
    symbolIast: 'nava grahāḥ',
    symbolEnglish: 'Nine Celestial Bodies (Navagrahas)',
    emoji: '🪐',
    theme: 'Cosmic & Nature',
    genderNote: 'त्रिषु लिङ्गेषु समानम्: नव ग्रहाः (M) / नव रसाः (M) / नवद्वाराणि (N - 9 bodily gates) / नवदुर्गाः (F).',
    alternateDev: 'नव रसाः',
    alternateEnglish: 'Nine Aesthetic Emotions (Navarasa in Poetry & Drama)',
    details: 'The nine celestial bodies of ancient astronomy (सूर्य, चन्द्र, मङ्गल, बुध, गुरु, शुक्र, शनि, राहु, केतु); the nine aesthetic rasas of human emotion; and the nine gates of the body temple (नवद्वारे पुरे देही).',
    shlokaLine: 'नव वदतु सर्वम् — नव ग्रहाः (Speak nine: Nine Celestial Grahas)',
    tags: ['planets', 'navagraha', 'rasa', 'emotions', 'nine', 'nava'],
  },
  {
    value: 10,
    devanagariNumeral: '१०',
    word: 'दश',
    iast: 'daśa',
    symbolDev: 'दश अङ्गुल्यः',
    symbolIast: 'daśa aṅgulyaḥ',
    symbolEnglish: 'Ten Digits / Fingers (Two Hands)',
    emoji: '👐',
    theme: 'Human Body & Senses',
    genderNote: 'त्रिषु लिङ्गेषु समानम्: दश अङ्गुल्यः (F - ten fingers/toes) / दशावताराः (M - 10 divine incarnations) / दश दिशः (F - 10 directions with zenith & nadir).',
    alternateDev: 'दशावताराः',
    alternateEnglish: 'Ten Divine Incarnations (Daśāvatāra of Viṣṇu)',
    details: 'The ten fingers of both hands (हस्ताङ्गुल्यः) which formed humanity’s primordial counting tool and inspired the decimal place-value system; and the ten divine cosmic incarnations (मत्स्य, कूर्म, वराह, नृसिंह, वामन, परशुराम, राम, कृष्ण, बुद्ध, कल्कि).',
    shlokaLine: 'दश वदतु सर्वम् — दश अङ्गुल्यः (Speak ten: Ten Digits/Fingers)',
    tags: ['digits', 'fingers', 'hands', 'avatars', 'ten', 'dasha', 'decimal'],
  },
];

